import { CreateAssetPayload } from '@/api/generatedApi';
import { useCreateAsset } from '@/hooks/api/assets/useCreateAsset';
import { LIST_FOLDER_CONTENTS_QUERY_KEY } from '@/hooks/api/folders/useFetchFolderContents';
import { FileMap, FileUploadStatus, FileWithStatus } from '@/types/upload';
import { transformPath } from '@/utils/path';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError, AxiosProgressEvent } from 'axios';
import { useEffect, useRef, useState } from 'react';
import { LIST_ASSETS_BY_STATE_QUERY_KEY } from './api/assets/useFetchAssetsByState';
import { ASSET_STATE_COUNTS_QUERY_KEY } from './api/assets/useGetAssetStateCounts';
import { GET_FOLDER_HIERARCHY_QUERY_KEY, useFolderHierarchy } from './api/folders/useFolderHierarchy';
import { GET_FOLDER_QUERY_KEY } from './api/folders/useGetFolder';
import { useFileDrop } from './useFileDrop';

const AUTO_UPLOAD_DELAY = 500;
const UPLOAD_BATCH_SIZE = 1;

let mainUploadInstance:
  | {
      fileInputRef: React.RefObject<HTMLInputElement | null>;
      folderInputRef: React.RefObject<HTMLInputElement | null>;
    }
  | undefined;

const handleFileUpload = () => {
  if (mainUploadInstance?.fileInputRef.current) {
    mainUploadInstance.fileInputRef.current.click();
  }
};

const handleFolderUpload = () => {
  if (mainUploadInstance?.folderInputRef.current) {
    mainUploadInstance.folderInputRef.current.click();
  }
};

type BulkCreateAssetParameters = {
  files: (Pick<CreateAssetPayload, 'file' | 'path'> & { originalPath?: string })[];
  metadata?: string;
  createMissingFolders?: boolean;
  onUploadProgress?: (progressEvent: AxiosProgressEvent, fileId: string) => void;
  onUploadSettled?: (result: BulkCreateAssetResult[number], item: BulkCreateAssetParameters['files'][number]) => void;
};

export type BulkCreateAssetResult = Array<{
  file: File;
  path: string;
  originalPath?: string;
  assetId?: string;
  folderId?: string;
  status: {
    state: FileUploadStatus.COMPLETED | FileUploadStatus.FAILED;
    code?: string;
    message?: string;
    traceId?: string;
  };
}>;

export const useAssetUpload = (options?: { onSuccess?: (results: BulkCreateAssetResult) => void }) => {
  const [files, setFiles] = useState<FileMap>({});
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [drawerOpen, setDrawerOpen] = useState(false);

  const fileInputReference = useRef<HTMLInputElement>(null);
  const folderInputReference = useRef<HTMLInputElement>(null);

  const queryClient = useQueryClient();

  const [targetFolderId, setTargetFolderId] = useState<string | undefined>();

  useEffect(() => {
    mainUploadInstance = {
      fileInputRef: fileInputReference,
      folderInputRef: folderInputReference,
    };

    return () => {
      if (mainUploadInstance?.fileInputRef === fileInputReference) {
        mainUploadInstance = undefined;
      }
    };
  }, []);

  const { folderPath } = useFolderHierarchy({ folderId: targetFolderId });
  const { createAsset } = useCreateAsset();

  const bulkCreateAsset = async ({
    files,
    metadata,
    createMissingFolders = true,
    onUploadProgress,
    onUploadSettled,
  }: BulkCreateAssetParameters): Promise<BulkCreateAssetResult> => {
    const results: BulkCreateAssetResult = [];

    for (let index = 0; index < files.length; index += UPLOAD_BATCH_SIZE) {
      const batch = files.slice(index, index + UPLOAD_BATCH_SIZE);

      const batchPromises = batch.map(async file => {
        try {
          const result = await createAsset({
            payload: {
              file: file.file,
              path: file.path,
              metadata,
              createMissingFolders: String(createMissingFolders),
            },
            options: {
              onUploadProgress: onUploadProgress ? event => onUploadProgress(event, file.path) : undefined,
            },
          });

          return {
            ...file,
            assetId: result.data?.id,
            folderId: result.data?.folderId,
            status: { state: FileUploadStatus.COMPLETED as const },
          };
        } catch (error) {
          return {
            ...file,
            status: {
              state: FileUploadStatus.FAILED as const,
              code: error instanceof AxiosError ? error.response?.data.message : 'Upload failed',
              message: error instanceof AxiosError ? error.response?.data.message : 'Upload failed',
              traceId: error instanceof AxiosError ? error.response?.data.traceId : undefined,
            },
          };
        }
      });

      const batchPromiseResults = await Promise.allSettled(batchPromises);

      for (const [index_, promiseResult] of batchPromiseResults.entries()) {
        const item = batch[index_];
        if (promiseResult.status === 'fulfilled') {
          const value = promiseResult.value;
          results.push(value);
          onUploadSettled?.(value, item);
        } else {
          const failure = {
            ...item,
            status: {
              state: FileUploadStatus.FAILED as const,
              code: 'Unexpected error during upload',
              message: 'Unexpected error during upload',
              traceId: undefined,
            },
          } as BulkCreateAssetResult[number];
          results.push(failure);
          onUploadSettled?.(failure, item);
        }
      }
    }

    if (results.some(result => result.status.state === FileUploadStatus.COMPLETED)) {
      queryClient.resetQueries({ queryKey: [LIST_FOLDER_CONTENTS_QUERY_KEY] });
      queryClient.resetQueries({ queryKey: [GET_FOLDER_QUERY_KEY] });
      queryClient.resetQueries({ queryKey: [LIST_ASSETS_BY_STATE_QUERY_KEY], exact: false });
      queryClient.resetQueries({ queryKey: [ASSET_STATE_COUNTS_QUERY_KEY] });
      queryClient.resetQueries({ queryKey: [GET_FOLDER_HIERARCHY_QUERY_KEY], exact: false });
    }

    return results;
  };

  const updateProgressForFile = (progressEvent: AxiosProgressEvent, fileId: string) => {
    if (progressEvent.total) {
      const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
      setUploadProgress(previous => ({ ...previous, [fileId]: progress }));
    }
  };

  const uploadFiles = async (validFiles: FileWithStatus[], folderId?: string) => {
    setTargetFolderId(folderId);
    if (validFiles.length === 0) return;

    try {
      setFiles(previousFiles => ({
        ...previousFiles,
        ...validFiles.reduce((accumulator, file) => {
          accumulator[file.path] = {
            ...file,
            status: { state: FileUploadStatus.UPLOADING },
          };
          setUploadProgress(previous => ({ ...previous, [file.path]: 0 }));
          return accumulator;
        }, {} as FileMap),
      }));

      const filesWithTransformedPaths = validFiles.map(file => ({
        file: file.file as File,
        path: transformPath(folderPath, file.path),
        originalPath: file.path,
      }));

      const results = await bulkCreateAsset({
        files: filesWithTransformedPaths,
        metadata: '',
        createMissingFolders: true,
        onUploadProgress: (progressEvent: AxiosProgressEvent, uploadPath: string) => {
          const fileWithTransformedPath = filesWithTransformedPaths.find(f => f.path === uploadPath);
          if (fileWithTransformedPath) {
            updateProgressForFile(progressEvent, fileWithTransformedPath.originalPath);
          }
        },
        onUploadSettled: (result, item) => {
          const originalPath = item.originalPath || item.path;
          setFiles(previousFiles => ({
            ...previousFiles,
            [originalPath]: {
              ...(previousFiles[originalPath] as FileWithStatus),
              status: result.status,
            },
          }));
        },
      });

      setFiles(previousFiles => ({
        ...previousFiles,
        ...results.reduce((accumulator: FileMap, result) => {
          const fileWithTransformedPath = filesWithTransformedPaths.find(f => f.path === result.path);
          const originalPath = fileWithTransformedPath?.originalPath || result.path;

          const { file } = result;
          const fileName = file.name;
          const previous = previousFiles[originalPath] as FileWithStatus | undefined;
          accumulator[originalPath] = {
            ...(previous || ({} as FileWithStatus)),
            id: fileName,
            name: fileName,
            path: originalPath,
            size: file.size,
            file: file,
            folderId: result.folderId ?? folderId,
            assetId: result.assetId,
            status: result.status,
          };
          return accumulator;
        }, {} as FileMap),
      }));

      const resultsWithOriginalPaths = results.map(result => {
        const match = filesWithTransformedPaths.find(f => f.path === result.path);
        return { ...result, originalPath: match?.originalPath ?? result.path };
      });
      options?.onSuccess?.(resultsWithOriginalPaths);
    } catch (error) {
      console.error('Upload failed:', error);

      setUploadProgress({});
    }
  };

  const handleFilesAdded = (valid: FileWithStatus[], invalid: FileWithStatus[], folderId?: string) => {
    setTargetFolderId(folderId);
    const selectedFiles = [...valid, ...invalid];

    if (invalid.length > 0) {
      setFiles(previousFiles => ({
        ...previousFiles,
        ...invalid.reduce((accumulator, file) => {
          accumulator[file.path] = file;
          accumulator[file.path].status = {
            ...file.status,
            state: FileUploadStatus.FAILED,
          };
          return accumulator;
        }, {} as FileMap),
      }));
    }

    if (selectedFiles.length > 0) {
      setDrawerOpen(true);
    }

    if (valid.length > 0) {
      setTimeout(() => {
        void uploadFiles(valid, folderId);
      }, AUTO_UPLOAD_DELAY);
    }
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setFiles({});
    setUploadProgress({});
  };

  const { fileInputProps, dropzoneProps, isDragActive } = useFileDrop({
    onFilesAdded: handleFilesAdded,
    folderId: targetFolderId,
  });

  useEffect(() => {
    const hasActiveUploads = Object.values(files).some(file => file.status.state === FileUploadStatus.UPLOADING);

    if (!hasActiveUploads) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      return;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [files]);

  return {
    files,
    uploadProgress,
    drawerOpen,
    handleFilesAdded,
    handleFileUpload,
    handleFolderUpload,
    handleDrawerClose,
    fileInputReference,
    folderInputReference,
    fileInputProps,
    dropzoneProps,
    isDragActive,
    setTargetFolderId,
  };
};
